import {
  Box,
  CircularProgress,
  LinearProgress,
  Pagination,
  Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CreateProduct, EditProduct, ProductEmptyState, ProductFilters, ProductViewHeader, ProductSelectionMode } from '../components';
import { CommonList, ListSelectionMode } from '../../common/components';
import type { Product } from '../../../types/product';
import { useProductData } from '../useProductData';
import { useProductMultiSelection } from '../multi-select/useProductMultiSelection';

export default function ProductViewPage() {
  const { id } = useParams<{ id: string }>();
  const companyId = id ? parseInt(id) : null;
  const navigate = useNavigate();

  const { data, pagination, filters, crud, dialogs } = useProductData({ companyId });
  const {
    data: { selectedProductsMap, limitError },
    actions: { handleToggleProductSelection, handleClearLimitError },
  } = useProductMultiSelection({
    products: data.products,
  });

  if (data.isFirstLoad) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {data.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {companyId && (
        <ProductViewHeader
          companyId={companyId}
          showFilters={filters.showFilters}
          onToggleFilters={() => filters.setShowFilters(!filters.showFilters)}
          hasProducts={data.products.length > 0}
          onCreateProduct={() => dialogs.setCreateDialogOpen(true)}
          onImportProducts={() => navigate(`/companies/${companyId}/products/import`)}
          hasActiveFilters={!!(filters.filterName || filters.filterCode) || !filters.showOnlyActive}
          selectedProductsMap={selectedProductsMap}
        />
      )}

      <ProductFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        filterCode={filters.filterCode}
        onFilterCodeChange={filters.setFilterCode}
        showOnlyActive={filters.showOnlyActive}
        onShowOnlyActiveChange={filters.setShowOnlyActive}
        onClearFilters={filters.handleClearFilters}
      />

      {limitError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={handleClearLimitError}
        >
          {limitError}
        </Alert>
      )}

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.products.length === 0 && !data.loading ? (
          <ProductEmptyState
            hasFilters={!!(filters.debouncedName || filters.debouncedCode)}
            selectionMode={ProductSelectionMode.PRODUCT_VIEW}
            onCreateProduct={() => dialogs.setCreateDialogOpen(true)}
          />
        ) : (
          <>
            <CommonList<Product>
              items={data.products}
              toListItem={(item) => ({
                id: item.id,
                primary: item.name,
                secondary: item.code,
                imageId: item.mainImageId,
                archived: !!item.deactivatedDate,
              })}
              selectionMode={ListSelectionMode.VIEW}
              selectedItemIds={Array.from(selectedProductsMap.keys())}
              onToggleSelect={handleToggleProductSelection}
              onDelete={crud.handleDeleteProduct}
              onRestore={crud.handleRestoreProduct}
              onEdit={crud.handleEditClick}
              onItemClick={(item) => navigate(`/companies/${companyId}/products/${item.id}`)}
              emptyMessage="Продукти не знайдено"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>

      {companyId && (
        <CreateProduct
          open={dialogs.createDialogOpen}
          companyId={companyId}
          onClose={() => dialogs.setCreateDialogOpen(false)}
          onSubmit={crud.handleCreateProduct}
        />
      )}

      <EditProduct
        open={dialogs.editDialogOpen}
        product={dialogs.productToEdit}
        onClose={() => {
          dialogs.setEditDialogOpen(false);
          dialogs.setProductToEdit(null);
        }}
        onSubmit={crud.handleUpdateProduct}
      />

      {dialogs.confirmDialog}
    </Box>
  );
}
